/**
 * @fileoverview Axios API Client Configuration
 * @description Centralized API client with interceptors and error handling
 * @module services/api/client
 */

import { APP_CONFIG } from '@/config';
import { HTTP_STATUS } from '@/constants';

/**
 * Creates and configures the API client.
 * Uses BFF session cookie for auth. Access token is fetched from
 * /api/auth/me and cached in memory.
 */
class ApiClient {
  constructor() {
    this.baseURL = APP_CONFIG.api.baseUrl;
    this.timeout = APP_CONFIG.api.timeout;
    this._cachedToken = null;
    this._redirecting = false;
  }

  /**
   * Set the access token (called by AuthContext after login).
   * @param {string|null} token
   */
  setAccessToken(token) {
    this._cachedToken = token;
  }

  /**
   * Get the current access token.
   * @returns {string|null}
   */
  _getAccessToken() {
    return this._cachedToken;
  }

  /**
   * Get default headers for requests
   * @returns {Object} Headers object
   */
  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    const token = this._getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Handle API response
   * @param {Response} response - Fetch response object
   * @returns {Promise<any>} Parsed response data
   */
  async handleResponse(response) {
    if (!response.ok) {
      if (response.status === HTTP_STATUS.UNAUTHORIZED) {
        // Guard against multiple concurrent 401s each triggering a redirect
        if (!this._redirecting) {
          this._redirecting = true;
          sessionStorage.removeItem('auth_session');
          window.location.href = '/login';
        }
        // Return a never-resolving promise so callers don't continue
        return new Promise(() => {});
      }
      
      const error = await response.json().catch(() => ({
        message: 'An unexpected error occurred',
      }));
      const errorMessage = error?.message || error?.error || `HTTP Error: ${response.status}`;

      const requestError = new Error(errorMessage);
      requestError.status = response.status;
      requestError.details = error;
      throw requestError;
    }

    // Handle empty responses
    if (response.status === HTTP_STATUS.NO_CONTENT) {
      return null;
    }

    return response.json();
  }

  /**
   * Make a GET request
   * @param {string} endpoint - API endpoint
   * @param {Object} params - Query parameters
   * @returns {Promise<any>} Response data
   */
  async get(endpoint, params = {}) {
    const url = new URL(`${this.baseURL}${endpoint}`, window.location.origin);
    Object.keys(params).forEach(key => url.searchParams.append(key, params[key]));

    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    return this.handleResponse(response);
  }

  /**
   * Make a GET request that returns a raw Blob (for file downloads).
   * @param {string} endpoint - API endpoint (may include query string)
   * @returns {Promise<Blob>} Response blob
   */
  async getRaw(endpoint) {
    const url = new URL(`${this.baseURL}${endpoint}`, window.location.origin);

    const response = await fetch(url, {
      method: 'GET',
      headers: { Authorization: this.getHeaders()['Authorization'] },
    });

    if (!response.ok) {
      if (response.status === HTTP_STATUS.UNAUTHORIZED) {
        if (!this._redirecting) {
          this._redirecting = true;
          sessionStorage.removeItem('auth_session');
          window.location.href = '/login';
        }
        return new Promise(() => {});
      }
      const error = await response.json().catch(() => ({
        message: 'Download failed',
      }));
      throw new Error(error.message || `HTTP Error: ${response.status}`);
    }

    return response.blob();
  }

  /**
   * Make a POST request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body
   * @returns {Promise<any>} Response data
   */
  async post(endpoint, data = {}) {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });

    return this.handleResponse(response);
  }

  /**
   * Make a PUT request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body
   * @returns {Promise<any>} Response data
   */
  async put(endpoint, data = {}) {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });

    return this.handleResponse(response);
  }

  /**
   * Make a PATCH request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body
   * @returns {Promise<any>} Response data
   */
  async patch(endpoint, data = {}) {
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });

    return this.handleResponse(response);
  }

  /**
   * Make a DELETE request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Optional request body
   * @returns {Promise<any>} Response data
   */
  async delete(endpoint, data = null) {
    const options = {
      method: 'DELETE',
      headers: this.getHeaders(),
    };
    if (data) {
      options.body = JSON.stringify(data);
    }
    const response = await fetch(`${this.baseURL}${endpoint}`, options);

    return this.handleResponse(response);
  }

  /**
   * Make a POST request with FormData (multipart/form-data)
   * @param {string} endpoint - API endpoint
   * @param {FormData} formData - FormData object
   * @returns {Promise<any>} Response data
   */
  async postFormData(endpoint, formData) {
    const headers = {};
    const token = this._getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // Do NOT set Content-Type — browser sets it with the boundary

    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'POST',
      headers,
      body: formData,
    });

    return this.handleResponse(response);
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
export default apiClient;
